import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { api, errMsg, watchSiteChanges } from '../lib/api';
import { CURRENCIES } from '../lib/constants';
import { toast } from 'sonner';

const CurrencyContext = createContext(null);
export const CurrencyProvider = ({ children }) => {
  const { user, setUser } = useAuth();
  const identity = user?.id;
  const [config, setConfig] = useState(null);
  const [loadedFor, setLoadedFor] = useState(null);
  const generation = useRef(0);
  const preferenceQueue = useRef(Promise.resolve());
  const preferenceActor = useRef(user?.id);
  preferenceActor.current = user?.id;
  const savePreferredCurrency = useCallback(code => {
    if (user?.kind !== 'customer') return;
    const principalId = user.id;
    setUser(previous => previous?.id === principalId ? { ...previous, preferredCurrency: code } : previous);
    preferenceQueue.current = preferenceQueue.current.catch(() => {}).then(() => preferenceActor.current === principalId ? api.patch('/preferences', { preferredCurrency: code }) : null)
      .catch(failure => { if (preferenceActor.current === principalId) toast.error(errMsg(failure)); });
  }, [user?.id, user?.kind, setUser]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    const request = ++generation.current;
    if (!identity) { setConfig(null); setLoading(false); return; }
    setLoading(true);
    try { const { data } = await api.get('/currencies'); if (request === generation.current) { setConfig(data); setLoadedFor(identity); setError(''); } }
    catch (failure) { if (request === generation.current) setError(errMsg(failure)); }
    finally { if (request === generation.current) setLoading(false); }
  }, [identity]);
  const invalidate = useCallback(() => { generation.current += 1; }, []);
  useEffect(() => { setConfig(null); setError(''); reload(); const stop = watchSiteChanges(reload); return () => { invalidate(); stop(); }; }, [reload, invalidate]);
  const value = useMemo(() => ({ config, loading, error, reload, savePreferredCurrency,
    preferredCurrency: user?.preferredCurrency,
    currencies: config?.currencies || CURRENCIES, codes: config?.permitted || CURRENCIES.map(currency => currency.code),
    defaultCurrency: config?.defaultCurrency || 'EUR' }), [config, loading, error, reload, savePreferredCurrency, user?.preferredCurrency]);
  const waiting = identity && (!config || loadedFor !== identity);
  return <CurrencyContext.Provider value={value}>{waiting ? <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6"><p data-testid="currency-config-loading" role={error ? 'alert' : 'status'}>{error || 'Loading currency settings…'}</p>{error && <button data-testid="currency-config-retry" className="underline" onClick={reload}>Retry</button>}</div> : children}</CurrencyContext.Provider>;
};

export const useCurrencies = () => useContext(CurrencyContext);
export const useSelectedCurrency = initial => {
  const { codes, defaultCurrency } = useCurrencies();
  const [selected, setSelected] = useState(initial || null);
  return [codes.includes(selected) ? selected : defaultCurrency, setSelected];
};